import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot } from '@angular/router';
import {
  BehaviorSubject,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  skip,
  tap,
} from 'rxjs';

/**
 * Service for handling routing-related operations and accessing route information.
 */
@Injectable({ providedIn: 'root' })
export class RoutingService {
  #activatedRoute = inject(ActivatedRoute);
  #activatedRoute$ = new BehaviorSubject<ActivatedRouteSnapshot | null>(null);
  #destroyRef = inject(DestroyRef);

  constructor() {
    this.#activatedRoute.queryParamMap
      .pipe(
        skip(1), // Skip the initial emission if necessary
        map(() =>
          this.#traverseToLeafRoute(this.#activatedRoute.root.snapshot.root),
        ),
        distinctUntilChanged((prev, curr) => prev === curr),
        tap((activatedRouteSnapshot) =>
          this.setActivatedRouteSnapshot(activatedRouteSnapshot),
        ),
        takeUntilDestroyed(),
      )
      .subscribe();

    this.#destroyRef.onDestroy(() => {
      this.#activatedRoute$.next(null);
      this.#activatedRoute$.complete();
    });
  }

  /**
   * Traverses the ActivatedRoute tree to find the deepest child route and return its snapshot.
   * @param {ActivatedRoute} route - The current activated route.
   * @returns {ActivatedRouteSnapshot} - The deepest ActivatedRouteSnapshot.
   */
  #traverseToLeafRoute(route: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
    let currentRoute = route;
    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
    }
    return currentRoute;
  }

  /**
   * Gets the current activated route snapshot.
   * @returns {ActivatedRouteSnapshot | null} The current activated route snapshot or null.
   */
  get activatedRouteSnapshot(): ActivatedRouteSnapshot | null {
    return this.#activatedRoute$.value;
  }

  /**
   * Gets the current activated route snapshot as an Observable.
   * @returns {ActivatedRouteSnapshot | null} The current activated route snapshot or null.
   */
  get activatedRouteSnapshot$(): Observable<ActivatedRouteSnapshot | null> {
    return this.#activatedRoute$.asObservable();
  }

  get activatedRoute(): ActivatedRoute {
    return this.#activatedRoute;
  }

  /**
   * Sets the current activated route.
   * @param {ActivatedRouteSnapshot} activatedRoute - The activated route to set.
   */
  setActivatedRouteSnapshot(activatedRoute: ActivatedRouteSnapshot): void {
    const { url, params, queryParams, data } = activatedRoute;
    this.#activatedRoute$.next({
      url,
      params,
      queryParams,
      data,
    } as ActivatedRouteSnapshot);
  }

  /**
   * Gets a specific property from the current activated route.
   * @param {T} key - The key of the property to retrieve.
   * @returns {ActivatedRouteSnapshot[T] | undefined} The value of the specified property or undefined.
   * @template T
   */
  getRouteProperty<T extends keyof ActivatedRouteSnapshot>(
    key: T,
  ): ActivatedRouteSnapshot[T] | undefined {
    return key === 'data'
      ? this.#activatedRoute$.value?.data['resolvedData']
      : this.#activatedRoute$.value?.[key];
  }

  /**
   * Gets an Observable of a specific property from the activated route.
   * @param {T} key - The key of the property to observe.
   * @returns {Observable<ActivatedRouteSnapshot[T] | undefined>} An Observable of the specified property.
   * @template T
   */
  getRoutePropertyAsObservable<T extends keyof ActivatedRouteSnapshot>(
    key: T,
  ): Observable<ActivatedRouteSnapshot[T] | undefined> {
    return this.#activatedRoute$.asObservable().pipe(
      filter(Boolean),
      map((snapshot) =>
        key === 'data' ? snapshot?.data['resolvedData'] : snapshot?.[key],
      ),
    );
  }

  /**
   * Gets a specific value from a route property.
   * @param {T} key - The key of the route property.
   * @param {string | string[]} matchCase - The key(s) to match within the property.
   * @returns {K | undefined} The matched value(s) or undefined.
   * @template K, T
   */
  getRoutePropertyWithValue<K, T extends keyof ActivatedRouteSnapshot>(
    key: T,
    matchCase: string | string[],
  ): K | undefined {
    const property =
      key === 'data'
        ? this.#activatedRoute$.value?.data['resolvedData']
        : this.#activatedRoute$.value?.[key];
    if (!property) return undefined;
    if (typeof property === 'string' || Array.isArray(property))
      return undefined;

    if (Array.isArray(matchCase)) {
      const result = matchCase.map((value) => {
        return {
          value: (property as any)[value] as K,
        } as unknown as K;
      });
      return result as unknown as K;
    }

    return (property as any)[matchCase] as K;
  }

  /**
   * Gets an Observable of a specific value from a route property.
   * @param {T} key - The key of the route property.
   * @param {string | string[]} matchCase - The key(s) to match within the property.
   * @returns {Observable<K | undefined>} An Observable of the matched value(s) or undefined.
   * @template K, T
   */
  getRoutePropertyWithValueAsObservable<
    K,
    T extends keyof ActivatedRouteSnapshot,
  >(key: T, matchCase: string | string[]): Observable<K | undefined> {
    return this.getRoutePropertyAsObservable(key).pipe(
      map((property) => {
        if (!property) return undefined;
        if (typeof property === 'string' || Array.isArray(property))
          return undefined;

        if (Array.isArray(matchCase)) {
          const result = matchCase.map((value) => {
            return {
              value: (property as any)[value] as K,
            } as unknown as K;
          });
          return result as unknown as K;
        }

        return (property as any)[matchCase] as K;
      }),
    );
  }
}
